import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Tokens } from 'api/models';
import { BASE_API_URL } from 'api/client';
import storage from 'utils/storageController';

// Axios config flag the response interceptor reads to skip its 401 → refresh
// fan-out for the refresh call itself. Today the refresh request goes through
// the bare `axios` instance (not `client`), so the interceptor wouldn't see
// it anyway; we still set the flag as defense-in-depth in case a future
// refactor routes the call through `client`.
export interface SkipAuthRefreshConfig extends AxiosRequestConfig {
    _skipAuthRefresh?: boolean;
}

// In-flight singleton. Any caller that races to refresh receives the same
// promise so the backend sees exactly one `auth.session_refreshed` per
// refresh cycle (the backend's idempotency_key is a belt; this is the
// suspenders). Cleared on a microtask after settlement so that handlers
// attached during settlement still observe the same promise reference, but
// a *later* refresh starts a fresh request.
let inflightRefresh: Promise<Tokens> | null = null;

function performRefresh(): Promise<Tokens> {
    const refreshTkn = storage.getRefreshToken();
    if (!refreshTkn) {
        return Promise.reject(new Error('No refresh token available'));
    }

    const config: SkipAuthRefreshConfig = {
        headers: { Authorization: 'Bearer ' + refreshTkn },
        _skipAuthRefresh: true,
    };

    return axios.get<Tokens>(`${BASE_API_URL}/refresh_token`, config).then((res) => res.data);
}

const LOCK_NAME = 'aliro-auth-refresh';

const supportsWebLocks =
    typeof navigator !== 'undefined' &&
    typeof navigator.locks !== 'undefined' &&
    typeof navigator.locks.request === 'function';

/**
 * Refresh the access/refresh token pair, coalescing concurrent callers into
 * a single in-flight HTTP request.
 *
 * - Within a tab: a module-level promise singleton means N parallel callers
 *   produce exactly 1 network call to `/api/refresh_token`.
 * - Across tabs (when the Web Locks API is available): a named lock
 *   serializes refreshes so two tabs can't rotate the same refresh token in
 *   parallel. Note we *serialize* rather than fully de-dup across tabs —
 *   each tab still needs its own in-memory `access_token`, and we don't
 *   persist that to localStorage. The serialization is enough to prevent
 *   the racing-rotation bug the audit log was catching.
 */
export function refreshSession(): Promise<Tokens> {
    if (inflightRefresh) return inflightRefresh;

    const run = supportsWebLocks
        ? navigator.locks.request(LOCK_NAME, () => performRefresh())
        : performRefresh();

    inflightRefresh = (run as Promise<Tokens>).finally(() => {
        // Release the singleton on the next microtask so callers awaiting
        // this promise still resolve against the same reference, then a
        // subsequent refresh cycle (e.g. the new access_token expires
        // later) can start fresh.
        queueMicrotask(() => {
            inflightRefresh = null;
        });
    });

    return inflightRefresh;
}

// Back-compat shim. Existing callers receive the same `AxiosResponse`-shaped
// envelope they used to get, but everyone now goes through the coalesced
// singleton above. New code should call `refreshSession()` directly.
export default function refreshToken(): Promise<AxiosResponse<Tokens>> {
    return refreshSession().then((data) => ({ data }) as AxiosResponse<Tokens>);
}
